/**
 * Submission helpers: URLs, fees, and AI verification results.
 *
 * formatItemLink normalizes product URLs (https, optional www); isValidImageUrl
 * validates image URLs and blocks private/internal hosts (SSRF guard);
 * calculateTotalWithFee applies the 8% platform fee; plus posted-date
 * formatting and SubmissionVerificationResult pass/failure processing.
 * Consumed by the submission create/display pages, ImageUrlsField,
 * PaymentMethodForm, and the admin training data page.
 */
import { SubmissionVerificationResult } from "@/types/submission";

export function formatItemLink(productUrl: string): string {
    // First trim any whitespace
    let processedUrl = productUrl.trim();

    if (!processedUrl) {
        throw new Error("Invalid URL format");
    }

    // Basic URL format validation before processing
    const urlPattern = /^(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+/;
    if (!urlPattern.test(processedUrl)) {
        throw new Error("Invalid URL format");
    }

    // Remove any existing protocol
    processedUrl = processedUrl.replace(/^(https?:\/\/)/, "");

    // Add www. only if there's no subdomain (e.g., not us.site.com, shop.site.com, etc.)
    // Check if URL is just domain.tld without any subdomain
    const parts = processedUrl.split("/")[0].split(".");
    const hasSubdomain = parts.length > 2; // domain.com = 2 parts, subdomain.domain.com = 3+ parts

    if (!processedUrl.startsWith("www.") && !hasSubdomain) {
        processedUrl = "www." + processedUrl;
    }

    // Add https:// protocol
    processedUrl = "https://" + processedUrl;

    try {
        // Final validation with URL constructor
        new URL(processedUrl);
        return processedUrl;
    } catch (e) {
        throw new Error("Invalid URL format");
    }
}

export function formatPostedDatetime(datetime: string) {
    const date = new Date(datetime);

    // Format with full month name, day, and year
    const formattedDate = date.toLocaleDateString("en-US", {
        month: "long", // "long" gives the full month name
        day: "numeric",
        year: "numeric",
    });

    return `submitted on ${formattedDate}`;
}

export function calculateTotalWithFee(amount: number): number {
    // Apply 8% fee and round up to the nearest cent
    return Math.ceil(amount * 1.08 * 100) / 100;
}

export function isValidImageUrl(url: string): boolean {
    if (!url.trim()) return true; // Empty URLs are valid (not required)

    try {
        const urlObj = new URL(url);

        // Only allow http and https schemes
        if (!["http:", "https:"].includes(urlObj.protocol)) {
            return false;
        }

        // Check for private/internal IPs and hostnames
        const hostname = urlObj.hostname.toLowerCase();

        // Block localhost and internal hostnames
        const blockedHostnames = [
            "localhost",
            "local",
            "internal",
            "intranet",
            "admin",
            "management",
            "api-internal",
            "metadata",
            "127.0.0.1",
            "0.0.0.0",
            "169.254.169.254", // AWS metadata
            "100.100.100.200", // Alibaba Cloud metadata
        ];

        for (const blocked of blockedHostnames) {
            if (hostname === blocked || hostname.endsWith(`.${blocked}`)) {
                return false;
            }
        }

        // Block private IP ranges (basic check)
        const ipPattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
        const match = hostname.match(ipPattern);
        if (match) {
            const [, a, b, c, d] = match.map(Number);
            // Check for private IP ranges
            if (a === 127) return false; // localhost
            if (a === 10) return false; // 10.0.0.0/8
            if (a === 172 && b >= 16 && b <= 31) return false; // 172.16.0.0/12
            if (a === 192 && b === 168) return false; // 192.168.0.0/16
            if (a === 169 && b === 254) return false; // link-local
            if (a === 0 && b === 0 && c === 0 && d === 0) return false; // all interfaces
        }

        return true;
    } catch {
        return false; // Invalid URL format
    }
}

export function submissionVerificationResultIsValid(result: SubmissionVerificationResult): boolean {
    return result.passed;
}

export function processSubmissionVerificationResultFailure(
    result: SubmissionVerificationResult
): string {
    if (result.failureReasons.length > 0) {
        return result.failureReasons.join("\n");
    }
    return "Verification failed for unknown reason";
}
